import { Code, Database, Lock, Smartphone, ArrowUpRight, Palette, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const services = [
  {
    icon: Code,
    title: "Web Development",
    description: "Our team crafts responsive, high-performance websites and e-commerce platforms using modern, scalable technologies.",
    features: ["Responsive Design", "E-Commerce", "Scalable Solutions"]
  },
  {
    icon: Smartphone,
    title: "Mobile App Development",
    description: "We build powerful and scalable mobile applications for iOS and Android that offer rich functionality and a flawless user experience.",
    features: ["iOS & Android", "Cross-Platform", "Rich Features"]
  },
  {
    icon: Palette,
    title: "UI & UX Design",
    description: "We create intuitive, engaging, and beautiful user interfaces that enhance satisfaction and drive user adoption.",
    features: ["User Research", "Prototyping", "Visual Design"]
  },
  {
    icon: Search,
    title: "SEO Optimization",
    description: "Boost your online visibility and attract organic traffic with our data-driven SEO strategies to improve your search engine rankings.",
    features: ["Keyword Research", "On-Page SEO", "Analytics"]
  },
  {
    icon: Lock,
    title: "Software Maintenance",
    description: "Our support services ensure your applications run smoothly with proactive monitoring, timely updates, and performance optimization.",
    features: ["Monitoring", "Updates", "Performance Tuning"]
  },
  {
    icon: Database,
    title: "CRM Platforms",
    description: "We build custom CRM solutions that empower you to manage customer data, track interactions, and automate sales pipelines for growth.",
    features: ["Data Management", "Sales Automation", "Analytics"]
  },
];

// Enhanced 3D Service Card Component
const ServiceCard = ({ service, index, hoveredIndex, setHoveredIndex }: any) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Motion values for mouse position
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth spring animations
  const rotateX = useSpring(
    useTransform(mouseY, [-0.5, 0.5], [15, -15]),
    { stiffness: 300, damping: 30 }
  );
  const rotateY = useSpring(
    useTransform(mouseX, [-0.5, 0.5], [-15, 15]),
    { stiffness: 300, damping: 30 }
  );

  // Scale effect
  const scale = useSpring(1, { stiffness: 300, damping: 30 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const centerX = rect.left + width / 2;
    const centerY = rect.top + height / 2;
    const mouseXPos = e.clientX - centerX;
    const mouseYPos = e.clientY - centerY;

    mouseX.set(mouseXPos / width);
    mouseY.set(mouseYPos / height);
    scale.set(1.05);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    scale.set(1);
    setHoveredIndex(null);
  };

  return (
    <motion.div
      ref={cardRef}
      style={{
        rotateX,
        rotateY,
        scale,
        transformStyle: "preserve-3d",
        perspective: 1000,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={() => setHoveredIndex(index)}
      whileHover={{ z: 50 }}
      className="relative h-full"
      role="article"
      aria-labelledby={`service-title-${index}`}
    >
      <Card className="group relative bg-gradient-to-br from-[#16181D] via-[#16181D] to-[#0B0C10] border-[#2C2F36] backdrop-blur-sm hover:border-[#0057FF]/50 hover:shadow-[0_0_30px_rgba(0,87,255,0.3)] transition-all duration-500 cursor-pointer overflow-hidden h-full flex flex-col">
        {/* Hover Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0057FF]/0 via-[#0057FF]/5 to-[#00C3FF]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Top Accent Line */}
        <motion.div
          className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0057FF] via-[#00C3FF] to-[#0057FF]"
          initial={{ scaleX: 0 }}
          whileHover={{ scaleX: 1 }}
          transition={{ duration: 0.5 }}
          style={{ originX: 0 }}
        />

        <CardContent className="p-8 flex flex-col h-full relative" style={{ transform: "translateZ(30px)" }}>
          {/* Icon */}
          <div className="flex items-start justify-between mb-6">
            <motion.div
              className="p-4 rounded-2xl bg-gradient-to-br from-[#0057FF]/20 to-[#00C3FF]/20 border-2 border-[#0057FF]/30 shadow-lg shadow-[#0057FF]/20"
              whileHover={{ 
                rotate: [0, -10, 10, -10, 0],
                scale: 1.1,
              }}
              transition={{ duration: 0.5 }}
              style={{ transform: "translateZ(50px)" }}
            >
              <service.icon className="h-10 w-10 text-[#00C3FF] drop-shadow-[0_0_10px_rgba(0,195,255,0.5)]" />
            </motion.div>
            <motion.div
              animate={{
                x: hoveredIndex === index ? [0, 5, 0] : 0,
                y: hoveredIndex === index ? [0, -5, 0] : 0,
              }}
              transition={{ duration: 0.5 }}
            >
              <ArrowUpRight className="h-6 w-6 text-[#8A8D92] group-hover:text-[#00C3FF] transition-colors" />
            </motion.div>
          </div>

          {/* Title */}
          <motion.h3
            id={`service-title-${index}`}
            className="text-2xl font-bold text-white group-hover:text-[#00C3FF] transition-colors duration-300 mb-4"
            style={{ transform: "translateZ(40px)" }}
          >
            {service.title}
          </motion.h3>

          {/* Description */}
          <motion.p
            className="text-[#B0B3B8] leading-relaxed mb-6 flex-grow"
            style={{ transform: "translateZ(30px)" }}
          >
            {service.description}
          </motion.p>

          {/* Features */}
          <motion.div
            className="pt-4 border-t border-[#2C2F36] mb-4"
            style={{ transform: "translateZ(20px)" }}
          >
            <div className="flex flex-wrap gap-2 min-h-[60px]">
              {service.features.map((feature: string, idx: number) => (
                <motion.span
                  key={idx}
                  className="text-xs px-3 py-1 rounded-full bg-[#16181D] text-[#B0B3B8] border border-[#2C2F36] group-hover:border-[#0057FF]/30 group-hover:bg-[#0057FF]/5 transition-all duration-300 h-fit"
                  whileHover={{ scale: 1.1, y: -2 }}
                  style={{ transform: "translateZ(10px)" }}
                >
                  {feature}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Learn More Link */}
          <motion.div
            className="flex items-center text-[#00C3FF] font-medium mt-auto"
            animate={{
              x: hoveredIndex === index ? 8 : 0,
            }}
            transition={{ duration: 0.3 }}
            style={{ transform: "translateZ(30px)" }}
          >
            <span className="text-sm">Learn more</span>
            <ArrowUpRight className="ml-1 h-4 w-4" />
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const Services = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section id="services" className="py-32 relative overflow-hidden bg-[#0B0C10]">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#16181D]/30 via-[#0B0C10] to-[#0B0C10]" />
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#0057FF]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#00C3FF]/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block mb-4">
            <span className="text-sm font-semibold uppercase tracking-wider text-[#00C3FF]">What We Do</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white">
            Our <span className="bg-gradient-to-r from-[#0057FF] via-[#00C3FF] to-[#0057FF] bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(0,195,255,0.3)]">Services</span>
          </h2>
          <p className="text-xl text-[#B0B3B8] max-w-3xl mx-auto leading-relaxed">
            Trionix Technologies services are designed to meet the specific needs and budget of each client, ensuring they receive a tailored web solution that helps them achieve their digital goals.
          </p>
        </motion.div>

        {/* Services Grid with 3D Cards */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    ease: "easeOut",
                  },
                },
              }}
            >
              <ServiceCard
                service={service}
                index={index}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <p className="text-muted-foreground mb-4">
            Don't see what you're looking for?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-lg font-semibold text-primary hover:text-secondary transition-colors group"
          >
            Let's discuss your custom needs
            <ArrowUpRight className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
